import React, { useState } from 'react';
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle } from "@/components/ui/card";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { AlertCircle, CheckCircle, Database, RefreshCw } from "lucide-react";
import { verifySupabaseConnections } from '@/services/supabaseHealthCheck';
import { useToast } from '@/hooks/use-toast';

// Définir une interface pour la structure du rapport
interface CheckResult {
  success: boolean;
  message?: string;
  error?: string;
  details?: any; // Garder 'any' pour details pour l'instant si la structure varie
  exists?: boolean;
}

interface HealthReport {
  overall: {
    success: boolean;
    message: string;
    status: string;
    details: Record<string, any>;
    error?: string;
  };
  checks: Record<string, CheckResult & { details?: { buckets?: string[]; accessResults?: Record<string, CheckResult> } }>;
  timestamp?: string;
}

const SupabaseVerification = () => {
  const [isLoading, setIsLoading] = useState(false);
  // Typer l'état report avec la nouvelle interface
  const [report, setReport] = useState<HealthReport | null>(null); 
  const [activeTab, setActiveTab] = useState("summary");

  const runVerification = async () => {
    setIsLoading(true);
    setReport(null);
    try {
      const healthResults = await verifySupabaseConnections();
      // Caster explicitement healthResults en HealthReport
      setReport(healthResults as HealthReport); 
      console.log('Supabase verification results:', healthResults);
    } catch (error) {
      console.error('Error running verification:', error);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <Card className="w-full">
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <Database className="h-5 w-5 text-primary" />
          Vérification Supabase
        </CardTitle>
        <CardDescription>
          Effectue une vérification complète des connexions Supabase pour s'assurer que tout fonctionne correctement.
        </CardDescription>
      </CardHeader>
      <CardContent>
        {report ? (
          <div className="space-y-4">
            <Alert variant={report.overall.success ? "success" : "destructive"}>
              <div className="flex items-start">
                {report.overall.success ? (
                  <CheckCircle className="h-5 w-5 mr-2 text-green-500" />
                ) : (
                  <AlertCircle className="h-5 w-5 mr-2" />
                )}
                <div>
                  <AlertTitle>
                    {report.overall.success ? "Vérification réussie" : "Problèmes détectés"}
                  </AlertTitle>
                  <AlertDescription>
                    {report.overall.message}
                  </AlertDescription>
                </div>
              </div>
            </Alert>
            
            <Tabs defaultValue="summary" value={activeTab} onValueChange={setActiveTab}>
              <TabsList className="grid grid-cols-4">
                <TabsTrigger value="summary">Résumé</TabsTrigger>
                <TabsTrigger value="storage">Stockage</TabsTrigger>
                <TabsTrigger value="services">Services</TabsTrigger>
                <TabsTrigger value="details">Détails</TabsTrigger>
              </TabsList>
              
              <TabsContent value="summary" className="space-y-4 mt-4">
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <div className="p-4 rounded-lg border bg-card text-card-foreground shadow-sm">
                    <h3 className="text-sm font-medium">Connexion</h3>
                    <p className={`text-xl font-semibold ${report.checks.basicConnection?.success ? 'text-green-500' : 'text-red-500'}`}>
                      {report.checks.basicConnection?.message || (report.checks.basicConnection?.success ? 'Connecté' : 'Échec')}
                    </p>
                  </div>
                  
                  <div className="p-4 rounded-lg border bg-card text-card-foreground shadow-sm">
                    <h3 className="text-sm font-medium">Accès Base de données</h3>
                    <p className={`text-xl font-semibold ${report.checks.databaseAccess?.success ? 'text-green-500' : 'text-red-500'}`}>
                      {report.checks.databaseAccess?.message || (report.checks.databaseAccess?.success ? 'Disponible' : 'Indisponible')}
                    </p>
                  </div>
                  
                  <div className="p-4 rounded-lg border bg-card text-card-foreground shadow-sm">
                    <h3 className="text-sm font-medium">Stockage</h3>
                    <p className={`text-xl font-semibold ${report.checks.storage?.success ? 'text-green-500' : 'text-red-500'}`}>
                      {report.checks.storage?.message || (report.checks.storage?.success ? 'Disponible' : 'Indisponible')}
                    </p>
                  </div>
                </div>
                
                <div className="p-4 rounded-lg border bg-card text-card-foreground shadow-sm">
                  <h3 className="text-sm font-medium mb-2">État des services</h3>
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    <div>
                      <h4 className="text-xs text-gray-500">Profils</h4>
                      <p className={`font-medium ${report.checks.profileService?.success ? 'text-green-500' : 'text-red-500'}`}>
                        {report.checks.profileService?.message || (report.checks.profileService?.success ? 'Fonctionnel' : 'Échec')}
                      </p>
                    </div>
                    <div>
                      <h4 className="text-xs text-gray-500">Messages</h4>
                      <p className={`font-medium ${report.checks.messageService?.success ? 'text-green-500' : 'text-red-500'}`}>
                        {report.checks.messageService?.message || (report.checks.messageService?.success ? 'Fonctionnel' : 'Échec')}
                      </p>
                    </div>
                    <div>
                      <h4 className="text-xs text-gray-500">Réservations</h4>
                      <p className={`font-medium ${report.checks.bookingService?.success ? 'text-green-500' : 'text-red-500'}`}>
                        {report.checks.bookingService?.message || (report.checks.bookingService?.success ? 'Fonctionnel' : 'Échec')}
                      </p>
                    </div>
                  </div>
                </div>
                
                <div className="p-4 rounded-lg border bg-card text-card-foreground shadow-sm">
                  <h3 className="text-sm font-medium mb-2">Horodatage</h3>
                  <p className="text-sm">{report.timestamp}</p>
                </div>
              </TabsContent>
              
              <TabsContent value="storage" className="space-y-4 mt-4">
                <div className="p-4 rounded-lg border bg-card text-card-foreground shadow-sm">
                  <h3 className="text-sm font-medium mb-2">État du stockage</h3>
                  <p className={`font-medium ${report.checks.storage?.success ? 'text-green-500' : 'text-red-500'}`}>
                    {report.checks.storage?.message || (report.checks.storage?.success ? 'Disponible' : 'Indisponible')}
                  </p>
                </div>
                
                <div className="p-4 rounded-lg border bg-card text-card-foreground shadow-sm">
                  <h3 className="text-sm font-medium mb-2">Buckets trouvés</h3>
                  {report.checks.storage?.details?.buckets?.length > 0 ? (
                    <ul className="list-disc pl-5">
                      {report.checks.storage.details.buckets.map((bucket: string, i: number) => (
                        <li key={i} className="text-sm">{bucket}</li>
                      ))}
                    </ul>
                  ) : (
                    <p className="text-sm text-gray-500">Aucun bucket trouvé.</p>
                  )}
                </div>
                
                <div className="p-4 rounded-lg border bg-card text-card-foreground shadow-sm">
                  <h3 className="text-sm font-medium mb-2">Détails d'accès aux buckets</h3>
                  {report.checks.storage?.details?.accessResults ? (
                    <div className="space-y-2">
                      {/* Typer explicitement le résultat de Object.entries */}
                      {Object.entries(report.checks.storage.details.accessResults).map(([bucketName, result]: [string, CheckResult]) => (
                        <div key={bucketName} className="flex items-center">
                          <div className={`w-2 h-2 rounded-full mr-2 ${result.success ? 'bg-green-500' : 'bg-red-500'}`}></div>
                          <span className="text-sm">{bucketName}: </span>
                          <span className={`text-sm ml-1 ${result.success ? 'text-green-500' : 'text-red-500'}`}>
                            {result.success ? 'Accessible' : 'Non accessible'}
                          </span>
                          {result.exists === false && (
                            <span className="text-sm ml-2 text-orange-500">(non existant)</span>
                          )}
                        </div>
                      ))}
                    </div>
                  ) : (
                    <p className="text-sm text-gray-500">Pas de détails disponibles.</p>
                  )}
                </div>
              </TabsContent>
              
              <TabsContent value="services" className="space-y-4 mt-4">
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <div className="p-4 rounded-lg border bg-card text-card-foreground shadow-sm">
                    <h3 className="text-sm font-medium mb-2">Service de profils</h3>
                    <p className={`font-medium ${report.checks.profileService?.success ? 'text-green-500' : 'text-red-500'}`}>
                      {report.checks.profileService?.message || (report.checks.profileService?.success ? 'Fonctionnel' : 'Échec')}
                    </p>
                    {report.checks.profileService?.details?.profileFetch && (
                      <div className="mt-2 text-xs space-y-1">
                        <div>Relations:</div>
                        <div className="pl-2">
                          <div className="flex items-center">
                            <div className={`w-2 h-2 rounded-full mr-1 ${report.checks.profileService.details.profileFetch.relationsCheck?.photos ? 'bg-green-500' : 'bg-red-500'}`}></div>
                            <span>Photos</span>
                          </div>
                          <div className="flex items-center">
                            <div className={`w-2 h-2 rounded-full mr-1 ${report.checks.profileService.details.profileFetch.relationsCheck?.services ? 'bg-green-500' : 'bg-red-500'}`}></div>
                            <span>Services</span>
                          </div>
                          <div className="flex items-center">
                            <div className={`w-2 h-2 rounded-full mr-1 ${report.checks.profileService.details.profileFetch.relationsCheck?.rates ? 'bg-green-500' : 'bg-red-500'}`}></div>
                            <span>Tarifs</span>
                          </div>
                        </div>
                      </div>
                    )}
                  </div>
                  
                  <div className="p-4 rounded-lg border bg-card text-card-foreground shadow-sm">
                    <h3 className="text-sm font-medium mb-2">Service de messages</h3>
                    <p className={`font-medium ${report.checks.messageService?.success ? 'text-green-500' : 'text-red-500'}`}>
                      {report.checks.messageService?.message || (report.checks.messageService?.success ? 'Fonctionnel' : 'Échec')}
                    </p>
                    <div className="mt-2 text-xs space-y-1">
                      <div className="flex items-center">
                        <div className={`w-2 h-2 rounded-full mr-1 ${report.checks.messageService?.details?.sendReceive?.success !== undefined ? 'bg-green-500' : 'bg-red-500'}`}></div>
                        <span>Envoi/Réception Test</span>
                      </div>
                      <div className="flex items-center">
                        <div className={`w-2 h-2 rounded-full mr-1 ${report.checks.messageService?.details?.subscription?.success !== undefined ? 'bg-green-500' : 'bg-red-500'}`}></div>
                        <span>Abonnement Temps Réel</span>
                      </div>
                    </div>
                  </div>
                  
                  <div className="p-4 rounded-lg border bg-card text-card-foreground shadow-sm">
                    <h3 className="text-sm font-medium mb-2">Service de réservations</h3>
                    <p className={`font-medium ${report.checks.bookingService?.success ? 'text-green-500' : 'text-red-500'}`}>
                      {report.checks.bookingService?.message || (report.checks.bookingService?.success ? 'Fonctionnel' : 'Échec')}
                    </p>
                  </div>
                </div>
              </TabsContent>
              
              <TabsContent value="details" className="space-y-4 mt-4">
                <div className="p-4 rounded-lg border bg-card text-card-foreground shadow-sm">
                  <h3 className="text-sm font-medium mb-2">Données brutes du rapport</h3>
                  <pre className="text-xs bg-muted p-2 rounded overflow-auto">
                    {JSON.stringify(report, null, 2)}
                  </pre>
                </div>
              </TabsContent>
            </Tabs>
          </div>
        ) : (
          <div className="flex flex-col items-center justify-center py-8">
            <p className="text-center text-muted-foreground mb-4">
              Exécutez la vérification pour analyser l'état des connexions Supabase.
            </p>
            <Button onClick={runVerification} disabled={isLoading}>
              {isLoading ? (
                <>
                  <RefreshCw className="mr-2 h-4 w-4 animate-spin" />
                  Vérification en cours...
                </>
              ) : (
                <>
                  <RefreshCw className="mr-2 h-4 w-4" />
                  Lancer la vérification
                </>
              )}
            </Button>
          </div>
        )}
      </CardContent>
      {report && (
        <CardFooter className="flex justify-between">
          <Button variant="outline" onClick={() => setReport(null)}>
            Réinitialiser
          </Button>
          <Button onClick={runVerification} disabled={isLoading}>
            {isLoading ? (
              <>
                <RefreshCw className="mr-2 h-4 w-4 animate-spin" />
                Actualisation...
              </>
            ) : (
              <>
                <RefreshCw className="mr-2 h-4 w-4" />
                Actualiser
              </>
            )}
          </Button>
        </CardFooter>
      )}
    </Card>
  );
};

export default SupabaseVerification;
